import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { Button } from '@/components/ui/Button'
import { useRegisterStudent } from '@/features/auth/hooks/useAuth'
import { passwordSchema, phoneSchema } from '@/features/auth/schemas'
import { AuthLayout } from '@/layouts/AuthLayout'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'

const schema = z
  .object({
    name: z.string().min(1, 'Name is required').max(255),
    email: z.email('Enter a valid email address'),
    phone: phoneSchema,
    password: passwordSchema,
    password_confirmation: z.string(),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  })

type FormValues = z.infer<typeof schema>

export function RegisterStudentPage() {
  const navigate = useNavigate()
  const register = useRegisterStudent()
  const [apiError, setApiError] = useState<unknown>(null)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', email: '', phone: '', password: '', password_confirmation: '' },
  })

  const onSubmit = handleSubmit((values) => {
    setApiError(null)
    register.mutate(values, {
      onSuccess: (user) => {
        void navigate('/verify-email', { state: { email: user.email } })
      },
      onError: (error) => setApiError(error),
    })
  })

  return (
    <AuthLayout title="Create Your Student Account" subtitle="Find your next opportunity" wide>
      <FormErrorSummary
        errors={errors}
        serverErrors={getFieldErrors(apiError)}
        generalMessage={apiError ? getErrorMessage(apiError) : null}
      />
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <div className="row g-3">
          <div className="col-md-6">
            <FormInput control={control} name="name" label="Full Name" required />
          </div>
          <div className="col-md-6">
            <FormInput control={control} name="email" label="Email" type="email" required />
          </div>
          <div className="col-md-6">
            <FormInput control={control} name="phone" label="Phone" type="tel" required />
          </div>
          <div className="col-md-6">
            <FormInput control={control} name="password" label="Password" type="password" required />
          </div>
          <div className="col-12">
            <FormInput
              control={control}
              name="password_confirmation"
              label="Confirm Password"
              type="password"
              required
            />
          </div>
          <div className="col-12">
            <Button type="submit" className="w-100" loading={register.isPending}>
              Create Account
            </Button>
          </div>
        </div>
      </form>
      <div className="text-center mt-3">
        <p className="text-muted mb-0">
          Already have an account?{' '}
          <Link to="/login" className="text-primary">
            Sign In
          </Link>
        </p>
      </div>
    </AuthLayout>
  )
}
